import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mic, Brain, Sparkles, CheckCircle2, Waves } from "lucide-react";

const scenes = [
  {
    raw: "hey can you pull up — uh, Sarah Martinez's chart? she was in last month for a crown prep on, I think tooth 30",
    inferred:
      "Opened Sarah Martinez's chart. Last visit: 3/14 — crown prep #30. Next step: seat crown.",
    tags: ["Open chart", "Sarah Martinez"],
    confidence: 96,
    artifacts: ["uh", "I think"],
  },
  {
    raw: "schedule Mrs. Johnson for a cleaning next Tuesday at two — actually make it 2:30 with [assistant voice] Stephanie",
    inferred:
      "Booked Jessica Johnson — Hygiene, Tue 4/28 at 2:30 PM with Stephanie. Confirmation SMS sent.",
    tags: ["Book appointment", "Hygiene"],
    confidence: 94,
    artifacts: ["[assistant voice]"],
  },
];

const AIDictationSlide = () => {
  const [sceneIdx, setSceneIdx] = useState(0);
  const [rawTyped, setRawTyped] = useState(0);
  const [inferredTyped, setInferredTyped] = useState(0);
  const [phase, setPhase] = useState("listening"); // listening, thinking, inferring, done

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      while (!cancelled) {
        for (let s = 0; s < scenes.length; s++) {
          if (cancelled) return;
          setSceneIdx(s);
          setRawTyped(0);
          setInferredTyped(0);
          setPhase("listening");

          const scene = scenes[s];
          await new Promise((r) => setTimeout(r, 600));

          // Type raw transcription
          for (let i = 0; i <= scene.raw.length; i += 3) {
            if (cancelled) return;
            setRawTyped(Math.min(i, scene.raw.length));
            await new Promise((r) => setTimeout(r, 22));
          }
          setRawTyped(scene.raw.length);
          await new Promise((r) => setTimeout(r, 400));

          // Thinking phase
          setPhase("thinking");
          await new Promise((r) => setTimeout(r, 1400));

          // Inferring phase
          setPhase("inferring");
          for (let i = 0; i <= scene.inferred.length; i += 3) {
            if (cancelled) return;
            setInferredTyped(Math.min(i, scene.inferred.length));
            await new Promise((r) => setTimeout(r, 20));
          }
          setInferredTyped(scene.inferred.length);
          setPhase("done");
          await new Promise((r) => setTimeout(r, 3800));
        }
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, []);

  const scene = scenes[sceneIdx];

  // Highlight artifacts in raw text
  const renderRaw = (txt) => {
    let remaining = txt;
    const nodes = [];
    let key = 0;
    while (remaining.length > 0) {
      let matched = false;
      for (const art of scene.artifacts) {
        if (remaining.startsWith(art)) {
          nodes.push(
            <span
              key={key++}
              className="bg-amber-100 text-amber-700 rounded px-0.5 italic font-semibold"
            >
              {art}
            </span>,
          );
          remaining = remaining.slice(art.length);
          matched = true;
          break;
        }
      }
      if (!matched) {
        // find next artifact
        const nextIdx = scene.artifacts
          .map((a) => remaining.indexOf(a))
          .filter((i) => i >= 0)
          .reduce((acc, i) => Math.min(acc, i), remaining.length);
        nodes.push(<span key={key++}>{remaining.slice(0, nextIdx)}</span>);
        remaining = remaining.slice(nextIdx);
      }
    }
    return nodes;
  };

  return (
    <div className="h-full w-full bg-[#f8fafc] flex flex-col items-center justify-center p-8 xl:p-12 relative overflow-hidden font-sans">
      {/* Ambient gradient blurs */}
      <div
        className="absolute top-[-200px] right-[-100px] w-[700px] h-[700px] rounded-full blur-[120px] opacity-30 pointer-events-none"
        style={{ background: "linear-gradient(135deg, #ddd6fe 0%, #c4b5fd 50%, #e0e7ff 100%)" }}
      />
      <div
        className="absolute bottom-[-200px] left-[-100px] w-[600px] h-[600px] rounded-full blur-[120px] opacity-25 pointer-events-none"
        style={{ background: "linear-gradient(135deg, #93c5fd 0%, #a5b4fc 50%, #c4b5fd 100%)" }}
      />
      <div className="absolute inset-0 bg-[radial-gradient(#cbd5e1_0.8px,transparent_0.8px)] [background-size:28px_28px] opacity-40 pointer-events-none" />

      <div className="w-full flex flex-col items-center relative z-10 max-w-[1200px]">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-6"
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-indigo-50 text-indigo-600 text-sm font-semibold mb-3 border border-indigo-100/60 shadow-sm">
            <Brain className="w-4 h-4" />
            <span>PMS Feature — AI Dictation</span>
          </div>
          <h2 className="text-3xl lg:text-[42px] font-bold text-slate-900 tracking-tight leading-tight">
            Just talk to your PMS.{" "}
            <span className="bg-gradient-to-r from-indigo-600 to-blue-600 bg-clip-text text-transparent">
              The co-pilot handles the rest.
            </span>
          </h2>
          <p className="text-slate-900 text-[22px] font-semibold mt-6 max-w-2xl mx-auto leading-relaxed">
            Skip the clicks. Ask Asha to pull up charts, book appointments, or update records —
            in natural speech, with no menus to navigate.
          </p>
        </motion.div>

        {/* Main card — raw → inferred */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.5 }}
          className="w-full grid grid-cols-[1fr_auto_1fr] gap-4 items-stretch"
        >
          {/* LEFT — Raw transcription */}
          <div className="rounded-[20px] bg-white border border-slate-200 shadow-[0_8px_60px_-12px_rgba(0,0,0,0.08)] p-5 flex flex-col">
            <div className="flex items-center gap-2 mb-3">
              <div className="relative">
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-slate-600 to-slate-700 flex items-center justify-center">
                  <Mic className="w-4 h-4 text-white" />
                </div>
                {phase === "listening" && (
                  <motion.div
                    animate={{ scale: [1, 1.5, 1], opacity: [0.6, 0, 0.6] }}
                    transition={{ duration: 1.8, repeat: Infinity }}
                    className="absolute inset-0 rounded-xl border-2 border-slate-400"
                  />
                )}
              </div>
              <div className="flex-1">
                <p className="text-[13px] font-bold text-slate-900">You, speaking to the co-pilot</p>
                <p className="text-[10px] text-slate-400">Natural voice — hesitations and all</p>
              </div>
              <span className="text-[9.5px] font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full border border-slate-200">
                MESSY
              </span>
            </div>

            {/* Waveform bar */}
            <div className="flex items-center gap-0.5 h-6 mb-2 px-1">
              {Array.from({ length: 40 }).map((_, i) => (
                <motion.span
                  key={i}
                  className="flex-1 bg-slate-300 rounded-sm"
                  animate={{
                    height:
                      phase === "listening"
                        ? [6, 12 + Math.abs((i * 13) % 14), 6]
                        : 4,
                  }}
                  transition={{
                    duration: 0.6,
                    repeat: phase === "listening" ? Infinity : 0,
                    delay: i * 0.02,
                  }}
                />
              ))}
            </div>

            <div className="bg-slate-50 rounded-xl border border-slate-100 p-4 flex-1 min-h-[140px]">
              <p className="text-[13.5px] text-slate-700 leading-relaxed">
                {renderRaw(scene.raw.slice(0, rawTyped))}
                {rawTyped < scene.raw.length && (
                  <span className="inline-block w-[1.5px] h-4 bg-slate-400 ml-0.5 animate-pulse align-middle" />
                )}
              </p>
            </div>
          </div>

          {/* MIDDLE — Inference engine */}
          <div className="flex flex-col items-center justify-center px-2">
            <motion.div
              animate={
                phase === "thinking"
                  ? { rotate: 360 }
                  : phase === "inferring"
                    ? { scale: [1, 1.1, 1] }
                    : {}
              }
              transition={
                phase === "thinking"
                  ? { duration: 2, repeat: Infinity, ease: "linear" }
                  : { duration: 1.2, repeat: Infinity }
              }
              className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500 via-blue-500 to-indigo-600 flex items-center justify-center shadow-xl shadow-indigo-500/30 mb-2"
            >
              <Brain className="w-7 h-7 text-white" />
            </motion.div>
            <p className="text-[10px] font-bold text-indigo-500 uppercase tracking-widest">
              Inference
            </p>
            <p className="text-[9px] text-slate-400">Engine</p>
            {/* Flow arrow dots */}
            <div className="flex flex-col items-center gap-1 mt-2">
              {[0, 1, 2].map((i) => (
                <motion.span
                  key={i}
                  className="w-1.5 h-1.5 rounded-full bg-indigo-400"
                  animate={{ opacity: [0.2, 1, 0.2] }}
                  transition={{
                    duration: 1.2,
                    repeat: Infinity,
                    delay: i * 0.2,
                  }}
                />
              ))}
            </div>
          </div>

          {/* RIGHT — Inferred clinical note */}
          <div className="rounded-[20px] bg-white border border-indigo-100 shadow-[0_8px_60px_-12px_rgba(99,102,241,0.15)] p-5 flex flex-col relative overflow-hidden">
            {/* Accent gradient */}
            <div
              className="absolute top-0 left-0 right-0 h-[3px]"
              style={{
                background: "linear-gradient(90deg, #6366f1 0%, #3b82f6 50%, #6366f1 100%)",
              }}
            />
            <div className="flex items-center gap-2 mb-3">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center shadow-lg shadow-indigo-500/20">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <div className="flex-1">
                <p className="text-[13px] font-bold text-slate-900">Co-pilot action</p>
                <p className="text-[10px] text-slate-400">Done — live in the PMS</p>
              </div>
              <AnimatePresence>
                {phase === "done" && (
                  <motion.span
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    className="flex items-center gap-1 text-[9.5px] font-bold text-emerald-600 bg-emerald-50 border border-emerald-100 px-2 py-0.5 rounded-full"
                  >
                    <CheckCircle2 className="w-3 h-3" />
                    {scene.confidence}% confidence
                  </motion.span>
                )}
              </AnimatePresence>
            </div>

            {/* Note body */}
            <div className="bg-gradient-to-br from-indigo-50/60 via-white to-blue-50/40 rounded-xl border border-indigo-50 p-4 flex-1 min-h-[140px] relative">
              {phase === "listening" && (
                <p className="text-[12px] text-slate-300 italic">
                  Waiting for speech…
                </p>
              )}
              {phase === "thinking" && (
                <div className="flex items-center gap-2 text-indigo-500">
                  <Waves className="w-4 h-4 animate-pulse" />
                  <span className="text-[12px] font-semibold">
                    Interpreting intent, executing in PMS…
                  </span>
                </div>
              )}
              {(phase === "inferring" || phase === "done") && (
                <p className="text-[13.5px] text-slate-800 leading-relaxed font-medium">
                  {scene.inferred.slice(0, inferredTyped)}
                  {inferredTyped < scene.inferred.length && (
                    <span className="inline-block w-[1.5px] h-4 bg-indigo-500 ml-0.5 animate-pulse align-middle" />
                  )}
                </p>
              )}
              {/* Action tags */}
              <AnimatePresence>
                {phase === "done" && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className="mt-3 flex items-center gap-1.5 pt-3 border-t border-indigo-100/70"
                  >
                    <span className="text-[9.5px] font-bold text-indigo-400 uppercase tracking-wider mr-1">
                      Action
                    </span>
                    {scene.tags.map((t, i) => (
                      <span
                        key={i}
                        className="text-[10.5px] font-bold text-indigo-700 bg-white border border-indigo-200 px-2 py-0.5 rounded-md shadow-sm"
                      >
                        {t}
                      </span>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </motion.div>

        {/* Bottom capability strip */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.5 }}
          className="w-full mt-4 grid grid-cols-4 gap-2.5"
        >
          {[
            { label: "Natural voice — no command syntax", icon: Waves },
            { label: "Handles mid-sentence corrections", icon: Brain },
            { label: "Executes actions across the PMS", icon: Sparkles },
            { label: "Chat back — clarify in voice", icon: Mic },
          ].map((s, i) => (
            <div
              key={i}
              className="rounded-xl bg-white/80 border border-slate-200 px-3 py-2.5 flex items-center gap-2"
            >
              <div className="w-7 h-7 rounded-lg bg-indigo-50 border border-indigo-100 flex items-center justify-center shrink-0">
                <s.icon className="w-3.5 h-3.5 text-indigo-600" />
              </div>
              <p className="text-[11.5px] font-semibold text-slate-700 leading-tight">
                {s.label}
              </p>
            </div>
          ))}
        </motion.div>
      </div>
    </div>
  );
};

export default AIDictationSlide;
